import { createLegacyInstance } from 'src/api';
import { appConfig } from 'src/lib/config/appConfig';

/** One table engine (server) holding a table. */
export interface TableServer {
    readonly id: string;
    /**
     * Human-readable server description. The backend has announced this field but does not publish
     * it yet, so this is null until the response carries one (see `parseTableServerInfo`).
     */
    readonly description: string | null;
}

/** Per-table metadata from the table proxy's `/tables` endpoint, normalized for the UI. */
export interface TableServerInfo {
    readonly servers: readonly TableServer[];
}

export type TablesInfo = Record</* table key */ string, TableServerInfo>;

const api = createLegacyInstance();

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const readDescription = (value: Record<string, unknown>): string | null => {
    const { description } = value;
    return typeof description === 'string' && description.length > 0 ? description : null;
};

/** A server entry is a bare id today; an object form may carry an id/name plus a description. */
const readServer = (entry: unknown): TableServer | null => {
    if (typeof entry === 'string') {
        return { id: entry, description: null };
    }
    if (!isRecord(entry)) {
        return null;
    }
    const id = [entry['id'], entry['name'], entry['server']].find(
        (candidate): candidate is string => typeof candidate === 'string',
    );
    return id === undefined ? null : { id, description: readDescription(entry) };
};

const readServers = (entries: unknown): TableServer[] =>
    Array.isArray(entries) ? entries.map(readServer).filter((server) => server !== null) : [];

/**
 * Normalizes one table's `/tables` entry. Today each entry is a `string[]` of server addresses;
 * the backend is extending each server with a human-readable `description`, so object entries
 * (`{ id | name | server: string; description?: string }`) are read too. Unreadable entries are
 * dropped rather than thrown.
 */
export const parseTableServerInfo = (entry: unknown): TableServerInfo => ({
    servers: readServers(Array.isArray(entry) ? entry : isRecord(entry) ? entry['servers'] : []),
});

/** Normalizes the whole `/tables` response; a malformed response yields no tables rather than throwing. */
export const parseTablesInfo = (raw: unknown): TablesInfo => {
    if (!isRecord(raw)) {
        return {};
    }
    return Object.fromEntries(Object.entries(raw).map(([tableKey, entry]) => [tableKey, parseTableServerInfo(entry)]));
};

/**
 * Query for getting all table servers
 * This endpoint is only available to superusers
 */
export const getTableServersQuery = (useNgProxy: boolean) => {
    return {
        queryKey: ['table-servers', useNgProxy],
        queryFn: () =>
            api
                .get<unknown>('/tables', { baseURL: useNgProxy ? appConfig.httpNgTableProxy : appConfig.tableProxy })
                .then((res) => parseTablesInfo(res.data)),
        staleTime: 60 * 1000, // Cache for 1 minute
        gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    };
};
